import { GetFlowsheetResponse, ICareElement } from "@/Flowsheets/Api/Contracts/GetFlowsheet";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { VitalSignCareElement } from "@/Flowsheets/Models/CareElements/VitalSignCareElement";
import { VitalSignExam } from "@/Flowsheets/Models/CareElements/VitalSignExam";
import { Flowsheet } from "@/Flowsheets/Models/Flowsheet";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { AgentDoseReviewedCareElement } from "../Models/CareElements/AgentDoseReviewedCareElement";
import { BooleanReviewedCareElement } from "../Models/CareElements/BooleanReviewedCareElement";
import { BooleanTextReviewedCareElement } from "../Models/CareElements/BooleanTextReviewedCareElement";
import { CareElement } from "../Models/CareElements/CareElement";
import { DateOnlyReviewedCareElement } from "../Models/CareElements/DateOnlyReviewedCareElement";
import { DecimalReviewedCareElement } from "../Models/CareElements/DecimalReviewedCareElement";
import { FirstLineReviewedCareElement } from "../Models/CareElements/FirstLineReviewedCareElement";
import { IntegerReviewedCareElement } from "../Models/CareElements/IntegerReviewedCareElement";
import { MultiSelectReviewedCareElement } from "../Models/CareElements/MultiSelectReviewedCareElement";
import { RiskFactorCareElement } from "../Models/CareElements/RiskFactorCareElement";
import { SubjectCareElement } from "../Models/CareElements/SubjectCareElement";
import { TextCareElement } from "../Models/CareElements/TextCareElement";
import { TextReviewedCareElement } from "../Models/CareElements/TextReviewedCareElement";
import { VitalSignCareElementName } from "../Models/CareElements/VitalSignCareElementName";
/**
 * Maps a GetFlowsheetResponse object to a Flowsheet object.
 *
 * @param getFlowsheetResponse - The response object containing flowsheet data.
 * @returns A new Flowsheet object with the mapped data.
 */
function getFlowsheetResponseToFlowsheet(getFlowsheetResponse: GetFlowsheetResponse): Flowsheet {
    return new Flowsheet({
        ...getFlowsheetResponse,
        CreationDate: new Date(getFlowsheetResponse.CreationDate),
        ApprovalDate: getFlowsheetResponse.ApprovalDate ? new Date(getFlowsheetResponse.ApprovalDate) : undefined,
        CareComponents: processCareComponents(getFlowsheetResponse?.CareComponents)
    });
}

function processCareComponents(careComponents: CareComponent[]): CareComponent[] {
    return careComponents?.map(component => {
        if (component.Name === "VitalSignsCareComponent") {
            const vitalSignCareElements = component.CareElements as VitalSignCareElement[];
            const systolicIndex = vitalSignCareElements.findIndex(e => e.Name === VitalSignCareElementName.BloodPressureSystolic);
            const diastolicIndex = vitalSignCareElements.findIndex(e => e.Name === VitalSignCareElementName.BloodPressureDiastolic);

            if (systolicIndex !== -1 && diastolicIndex !== -1) {
                const systolic = vitalSignCareElements[systolicIndex];
                const diastolic = vitalSignCareElements[diastolicIndex];
                const mergedBloodPressure = createMergedBloodPressure(systolic, diastolic);

                component.CareElements = [
                    ...vitalSignCareElements.slice(0, systolicIndex),
                    mergedBloodPressure,
                    ...vitalSignCareElements.slice(systolicIndex + 1).filter(e => e.Name !== VitalSignCareElementName.BloodPressureDiastolic)
                ];
            }
        }
        else if (component.Name === FlowsheetConstants.RiskFactors.CareComponentName) {
            component.CareElements = component.CareElements
                .map(element => {
                    if (element.CareElementType === CareElementType.RiskFactor)
                        return new RiskFactorCareElement(element);

                    return null;
                })
                .filter(element => element !== null);
        }
        else {
            component.CareElements = component.CareElements.map(mapCareElements);
        }
        return component;
    });
}

function mapCareElements(careElement: ICareElement): CareElement {
    switch (careElement.CareElementType) {
        case CareElementType.Text:
            return new TextCareElement(careElement);
        case CareElementType.BooleanReviewed:
            return new BooleanReviewedCareElement(careElement);
        case CareElementType.BooleanTextReviewed:
            return new BooleanTextReviewedCareElement(careElement);
        case CareElementType.IntegerReviewed:
            return new IntegerReviewedCareElement(careElement);
        case CareElementType.TextReviewed:
            return new TextReviewedCareElement(careElement);
        case CareElementType.DecimalReviewed:
            return new DecimalReviewedCareElement(careElement);
        case CareElementType.MultiSelectReviewed:
            return new MultiSelectReviewedCareElement(careElement);
        case CareElementType.DateOnlyReviewed:
            return new DateOnlyReviewedCareElement(careElement);
        case CareElementType.Subject:
            return new SubjectCareElement(careElement);
        case CareElementType.FirstLineReviewed:
            return new FirstLineReviewedCareElement(careElement);
        case CareElementType.AgentDoseReviewed:
            return new AgentDoseReviewedCareElement(careElement);
        default:
            return careElement;
    }
}

function createMergedBloodPressure(
    systolic: VitalSignCareElement,
    diastolic: VitalSignCareElement
): VitalSignCareElement {
    return new VitalSignCareElement({
        Name: VitalSignCareElementName.BloodPressure,
        Frequency: systolic.Frequency ?? diastolic.Frequency,
        TargetRange: { LowerBound: systolic.TargetRange?.LowerBound, UpperBound: diastolic.TargetRange?.LowerBound },
        Operator: systolic.Operator ?? diastolic.Operator,
        Unit: systolic.Unit ?? diastolic.Unit,
        CareElementType: CareElementType.VitalSigns,
        Exams: systolic.Exams.map((systolicExam, index) => {
            const diastolicExam = diastolic.Exams[index];
            return new VitalSignExam({
                Value: systolicExam?.Value ?? null,
                Value2: diastolicExam?.Value ?? null,
                Unit: systolicExam.Unit,
                Date: systolicExam.Date
            });
        })
    });
}

export default getFlowsheetResponseToFlowsheet;
